import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { logger } from '@/utils/logger/logger';
import { Region } from '@heidi/schema/ml-scribe';
import { OpenFeature } from '@openfeature/react-sdk';
import { z } from 'zod';

// Country name -> Region enum value, keyed by the enum value (case-insensitive)
// so the Flagsmith JSON payload can use any casing (e.g. "jp-az1").
const REGION_BY_VALUE = new Map(
  Object.values(Region).map((region) => [region.toUpperCase(), region])
);

const RegionalSignupMapSchema = z.record(z.string(), z.string());

// Maps a signup country to a dedicated regional stack. Driven by the Flagsmith
// JSON flag `config-regional-signup`, whose value looks like:
//   { "Japan": "jp-az1", "United Arab Emirates": "uae-az1" }
// An override applies only when the flag is enabled AND the country is present
// in the payload; region codes are matched case-insensitively against the
// `Region` enum and unknown codes are dropped. Consumed at the
// `countriesToRegion` resolution chokepoint so every signup region
// write/redirect/trait honours it.
//
// The enabled check (`getBooleanValue` -> Flagsmith `hasFeature`) is required
// because `getObjectValue` (-> Flagsmith `getValue`) returns the payload even
// when the flag is toggled off.
//
// This lives outside `@/components/constants/flags` on purpose: that module is
// partially `jest.mock`ed across the suite, and `countriesToRegion` (a widely
// imported map) reads this on every lookup — importing it from the mocked
// module would crash any test that omits this export. Reading OpenFeature here
// keeps the lookup resilient (an unconfigured provider returns the default).
//
// OpenFeature / Flagsmith.
export const GET_REGIONAL_SIGNUP_MAP = (): Record<string, Region> => {
  const client = OpenFeature.getClient(FEATURE_FLAG_DOMAIN);
  if (!client.getBooleanValue('config-regional-signup', false)) {
    return {};
  }

  const parsed = RegionalSignupMapSchema.safeParse(
    client.getObjectValue('config-regional-signup', {})
  );
  if (!parsed.success) {
    logger.error('Invalid config-regional-signup payload', {
      error: parsed.error,
    });
    return {};
  }

  const regionByCountry: Record<string, Region> = {};
  for (const [country, code] of Object.entries(parsed.data)) {
    const region = REGION_BY_VALUE.get(code.toUpperCase());
    if (region) regionByCountry[country] = region;
  }
  return regionByCountry;
};
